import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Editor } from '@tiptap/core'
import { ReactRenderer } from '@tiptap/react'
import Suggestion, { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion'
import { PluginKey } from '@tiptap/pm/state'
import { SuggestionItem } from './types.js'
import { computePosition, offset, autoUpdate } from '@floating-ui/dom'

interface SuggestionMenuProps {
  editor: Editor
  char: string
  items: (props: { query: string; editor: Editor }) => SuggestionItem[]
  children: (props: any) => React.ReactElement
}

let popupElement: HTMLElement | null = null
let cleanupAutoUpdate: (() => void) | null = null
let isPopupVisible = false

// Helper functions to manage Floating UI popup
const initPopup = () => {
  if (!popupElement && typeof document !== 'undefined') {
    popupElement = document.createElement('div')
    popupElement.style.position = 'fixed'
    popupElement.style.zIndex = '10000'
    popupElement.style.maxWidth = '16rem'
    popupElement.style.visibility = 'hidden'
    popupElement.style.opacity = '0'
    popupElement.style.transition = 'opacity 200ms'
    popupElement.className = 'slash-command-menu'
    document.body.appendChild(popupElement)
  }
  return popupElement
}

const showPopup = () => {
  if (popupElement && !isPopupVisible) {
    isPopupVisible = true
    popupElement.style.display = 'block'
    popupElement.style.visibility = 'visible'
    popupElement.style.opacity = '1'
  }
}

const hidePopup = () => {
  if (popupElement) {
    isPopupVisible = false
    popupElement.style.visibility = 'hidden'
    popupElement.style.opacity = '0'
    // Use setTimeout to wait for transition before hiding completely
    setTimeout(() => {
      if (popupElement && !isPopupVisible) {
        popupElement.style.display = 'none'
      }
    }, 200)
  }
}

const updatePopupPosition = async (getReferenceRect: () => DOMRect) => {
  if (!popupElement || !isPopupVisible) return

  try {
    const rect = getReferenceRect()

    const virtualElement = {
      getBoundingClientRect: () => rect,
    }

    const { x, y } = await computePosition(virtualElement, popupElement, {
      placement: 'bottom-start',
      strategy: 'fixed',
      middleware: [offset({ mainAxis: 8, crossAxis: 16 })],
    })

    popupElement.style.left = `${x}px`
    popupElement.style.top = `${y}px`
  } catch (error) {
    console.error('Error updating popup position:', error)
  }
}

export const SuggestionMenu = ({ editor, char, items, children }: SuggestionMenuProps) => {
  const [suggestionProps, setSuggestionProps] = useState<SuggestionProps | null>(null)
  const suggestionRef = useRef<any>(null)

  useEffect(() => {
    if (!editor) return

    // Clean up existing suggestion if it exists
    if (suggestionRef.current) {
      editor.unregisterPlugin(suggestionRef.current)
    }

    const suggestion = Suggestion({
      editor,
      char,
      allowSpaces: true,
      startOfLine: true,
      pluginKey: new PluginKey('slash-suggestion'),
      allow: ({ state, range }) => {
        const $from = state.doc.resolve(range.from)
        const isRootDepth = $from.depth === 1
        const isParagraph = $from.parent.type.name === 'paragraph'
        const isStartOfNode = $from.parent.textContent?.charAt(0) === char
        const isInColumn = editor.isActive('column')

        const afterContent = $from.parent.textContent?.substring(
          $from.parent.textContent?.indexOf(char),
        )
        const isValidAfterContent = !afterContent?.endsWith('  ')

        // Check if drag handle is locked (ContentItemMenu is open)
        const isDragHandleLocked = (editor.storage as any).dragHandle?.lockDragHandle

        const shouldAllow =
          ((isRootDepth && isParagraph && isStartOfNode) ||
            (isInColumn && isParagraph && isStartOfNode)) &&
          isValidAfterContent &&
          !isDragHandleLocked

        return shouldAllow
      },
      command: ({ editor, props }: { editor: Editor; props: any }) => {
        const { view, state } = editor
        const { $head, $from } = view.state.selection

        const end = $from.pos
        const from = $head?.nodeBefore
          ? end -
            ($head.nodeBefore.text?.substring($head.nodeBefore.text?.indexOf(char)).length ?? 0)
          : $from.start()

        const tr = state.tr.deleteRange(from, end)
        view.dispatch(tr)

        props.onSelect({ editor })
        view.focus()
      },
      items: ({ query }: { query: string }) => {
        return items({ query, editor })
      },
      render: () => {
        let component: any
        let scrollHandler: (() => void) | null = null

        return {
          onStart: (props: SuggestionProps) => {
            setSuggestionProps(props)

            // Set slash command as active in storage
            ;(props.editor.storage as any).slashSuggestion = {
              ...(props.editor.storage as any).slashSuggestion,
              isActive: true,
            }

            // Temporarily disable drag handle to prevent interference
            props.editor.commands.setMeta('hideDragHandle', true)

            component = new ReactRenderer(children, {
              props,
              editor: props.editor,
            })

            const { view } = props.editor

            // Initialize the popup if needed
            initPopup()

            if (popupElement) {
              // Clear previous content
              popupElement.innerHTML = ''
              // Append the component
              popupElement.appendChild(component.element)

              // Set up positioning
              const getReferenceRect = () => {
                const { state } = view
                const { selection } = state
                const coords = view.coordsAtPos(selection.from)
                const node = view.nodeDOM(selection.from)

                if (node && node instanceof Element) {
                  const rect = node.getBoundingClientRect()
                  return new DOMRect(coords.left, coords.top, 0, 0)
                }

                return new DOMRect(coords.left, coords.top, 0, 0)
              }

              // Set up auto-update for positioning
              if (cleanupAutoUpdate) cleanupAutoUpdate()
              cleanupAutoUpdate = autoUpdate(
                { getBoundingClientRect: getReferenceRect },
                popupElement,
                async () => {
                  await updatePopupPosition(getReferenceRect)
                },
              )

              scrollHandler = () => {
                updatePopupPosition(getReferenceRect)
              }

              view.dom.parentElement?.addEventListener('scroll', scrollHandler)

              // Show the popup
              showPopup()
            }
          },

          onUpdate(props: SuggestionProps) {
            setSuggestionProps(props)
            component.updateProps(props)

            // Update positioning for the popup
            const { view } = props.editor
            const { state } = view
            const { selection } = state
            const coords = view.coordsAtPos(selection.from)

            const getReferenceRect = () => {
              return new DOMRect(coords.left, coords.top, 0, 0)
            }

            updatePopupPosition(getReferenceRect)
          },

          onKeyDown(props: SuggestionKeyDownProps) {
            if (props.event.key === 'Escape') {
              hidePopup()
              // Return focus to the editor
              setTimeout(() => {
                editor.view.focus()
              }, 0)
              return true
            }

            // Only move focus to menu for arrow keys
            if (props.event.key === 'ArrowDown' || props.event.key === 'ArrowUp') {
              if (!isPopupVisible) {
                showPopup()
              }

              // Focus the menu element for navigation
              setTimeout(() => {
                const menuElement = component.element.querySelector('[tabindex="0"]')
                if (menuElement) {
                  menuElement.focus()
                }
              }, 0)

              return false
            }

            // Handle Enter key when menu is focused
            if (props.event.key === 'Enter') {
              if (!isPopupVisible) {
                showPopup()
              }

              // Focus the menu element and let it handle Enter
              setTimeout(() => {
                const menuElement = component.element.querySelector('[tabindex="0"]')
                if (menuElement) {
                  menuElement.focus()
                  // Trigger Enter key on the menu
                  const enterEvent = new KeyboardEvent('keydown', {
                    key: 'Enter',
                    code: 'Enter',
                    keyCode: 13,
                    which: 13,
                    bubbles: true,
                    cancelable: true,
                  })
                  menuElement.dispatchEvent(enterEvent)
                }
              }, 0)

              return false
            }

            // For any other key (letters, numbers, etc.), ensure editor has focus
            // This handles the case where user types after opening the menu
            if (!isPopupVisible) {
              showPopup()
            }

            // Ensure editor has focus for typing
            setTimeout(() => {
              editor.view.focus()
            }, 0)

            // Let the editor handle the key normally
            return false
          },

          onExit(props) {
            // Set slash command as inactive in storage
            ;(props.editor.storage as any).slashSuggestion = {
              ...(props.editor.storage as any).slashSuggestion,
              isActive: false,
            }

            // Re-enable drag handle
            props.editor.commands.setMeta('hideDragHandle', false)

            // Clean up scroll handler first
            if (scrollHandler) {
              const { view } = props.editor
              view.dom.parentElement?.removeEventListener('scroll', scrollHandler)
              scrollHandler = null
            }

            // Clean up auto-update listener
            if (cleanupAutoUpdate) {
              cleanupAutoUpdate()
              cleanupAutoUpdate = null
            }

            // Hide the popup
            hidePopup()

            // Clean up component
            component?.destroy?.()
            component = null
            setSuggestionProps(null)

            // Clear popup content
            if (popupElement) {
              popupElement.innerHTML = ''
            }

            // Return focus to the editor
            setTimeout(() => {
              props.editor.view.focus()
            }, 0)
          },
        }
      },
    })

    editor.registerPlugin(suggestion)
    suggestionRef.current = suggestion

    return () => {
      if (suggestionRef.current) {
        editor.unregisterPlugin(suggestionRef.current)
        suggestionRef.current = null
      }
    }
  }, [editor, char, items])

  return null
}
